/**
 * Genesis Wizard
 * 
 * Bootstraps a new eStream network from empty state.
 * Shows when /api/genesis/status returns initialized: false.
 * 
 * Steps:
 * 1. Governance Configuration (key holders, thresholds)
 * 2. Time Delays (deploy timelock, upgrade timelock)
 * 3. Fail-Closed Rules (auto-halt triggers, recovery)
 * 4. Bootstrap Nodes (minimum nodes, cloud diversity)
 * 5. Signature Collection (async from key holders via Spark)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparkRenderer } from '../components/SparkRenderer';
// Genesis hook - imported from SDK
// Using direct dist import to avoid needing npm reinstall during dev
import { 
  useGenesisKeyHolder, 
  type KeyHolderResult,
} from '../../../../packages/sdk-browser/dist/genesis.js';
import './GenesisWizard.css';

// ============================================
// Types
// ============================================

interface KeyHolder {
  id: string;
  name: string;
  publicKeyHex: string;
  walletId: string;
  addedAt: string;
}

interface Signature {
  keyHolderId: string;
  signatureHex: string;
  signedAt: string;
  verified: boolean;
}

interface GenesisConfig {
  // Governance
  keyHolders: KeyHolder[];
  normalThreshold: number;
  criticalThreshold: number;
  actionCooldownHours: number;
  
  // Time Delays
  deployTimelockMinutes: number;
  upgradeTimelockHours: number;
  emergencyResponseMinutes: number;
  
  // Fail-Closed Rules
  failedDeploymentThreshold: number;
  nodeFailureThreshold: number;
  requireCriticalForRecovery: boolean;
  
  // Bootstrap Nodes
  minimumNodes: number;
  requiredCloudProviders: number;
  requiredRegions: number;
}

interface GenesisProposal {
  proposalId: string;
  configHash: string;
  signatures: Signature[];
  createdAt: string;
  status: 'pending' | 'ready' | 'completed' | 'failed';
}

const DEFAULT_CONFIG: GenesisConfig = {
  keyHolders: [],
  normalThreshold: 2,
  criticalThreshold: 4,
  actionCooldownHours: 24,
  
  deployTimelockMinutes: 60,
  upgradeTimelockHours: 72,
  emergencyResponseMinutes: 15,
  
  failedDeploymentThreshold: 3,
  nodeFailureThreshold: 50,
  requireCriticalForRecovery: true,
  
  minimumNodes: 4,
  requiredCloudProviders: 2,
  requiredRegions: 2,
};

type Step = 'governance' | 'delays' | 'failclosed' | 'bootstrap' | 'signatures';

// ============================================
// Helpers (module-level)
// ============================================

function getNetworkName(): string {
  if (typeof window === 'undefined') return 'alpha-devnet';
  const hostname = window.location.hostname;
  const match = hostname.match(/console\.(.+)\.estream\./);
  return match ? match[1] : 'alpha-devnet';
}

function getNetworkType(): 'mainnet' | 'testnet' | 'devnet' {
  const name = getNetworkName();
  if (name.includes('mainnet')) return 'mainnet';
  if (name.includes('testnet')) return 'testnet';
  return 'devnet';
}

// ============================================
// Component
// ============================================

export default function GenesisWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('governance');
  const [config, setConfig] = useState<GenesisConfig>(DEFAULT_CONFIG);
  const [proposal, setProposal] = useState<GenesisProposal | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Spark state for adding key holders
  const [showSparkScanner, setShowSparkScanner] = useState(false);
  const [pendingKeyHolderName, setPendingKeyHolderName] = useState('');
  
  const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
  
  // Real Spark integration for key holder registration
  const { 
    state: keyHolderState,
    startChallenge: startKeyHolderChallenge,
    cancel: cancelKeyHolderChallenge,
    timeRemaining: keyHolderTimeRemaining,
    challengeJson: keyHolderChallengeJson,
  } = useGenesisKeyHolder({
    networkName: getNetworkName(),
    edgeUrl,
    onRegistered: (result: KeyHolderResult) => {
      // Key holder successfully registered via Spark
      const newKeyHolder: KeyHolder = {
        id: `GKH-${config.keyHolders.length + 1}`,
        name: pendingKeyHolderName,
        publicKeyHex: result.publicKey,
        walletId: result.walletId,
        addedAt: new Date().toISOString(),
      };
      
      updateConfig('keyHolders', [...config.keyHolders, newKeyHolder]);
      setShowSparkScanner(false);
      setPendingKeyHolderName('');
    },
    onError: (err: string) => {
      console.error('[Genesis] Key holder registration error:', err);
      setError(`Failed to register key holder: ${err}`);
    },
  });
  
  const steps: { id: Step; title: string; description: string }[] = [
    { id: 'governance', title: 'Governance', description: 'Configure key holders and voting thresholds' },
    { id: 'delays', title: 'Time Delays', description: 'Set timelocks for operations' },
    { id: 'failclosed', title: 'Fail-Closed', description: 'Configure auto-halt triggers' },
    { id: 'bootstrap', title: 'Bootstrap', description: 'Define minimum infrastructure' },
    { id: 'signatures', title: 'Signatures', description: 'Collect key holder signatures' },
  ];
  
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  
  // ============================================
  // Config Updates
  // ============================================
  
  const updateConfig = <K extends keyof GenesisConfig>(key: K, value: GenesisConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };
  
  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id);
    }
  };
  
  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  };
  
  // ============================================
  // Key Holder Management
  // ============================================
  
  const startAddKeyHolder = () => {
    const defaultName = `Key Holder ${config.keyHolders.length + 1}`;
    setPendingKeyHolderName(defaultName);
    setShowSparkScanner(true);
    // Start the real Spark challenge
    startKeyHolderChallenge(config.keyHolders.length + 1, defaultName);
  };
  
  const cancelAddKeyHolder = () => {
    cancelKeyHolderChallenge();
    setShowSparkScanner(false);
    setPendingKeyHolderName('');
  };
  
  // Update challenge when name changes
  const updateKeyHolderName = (name: string) => {
    setPendingKeyHolderName(name);
    // Restart challenge with new name if already pending
    if (keyHolderState.status === 'pending') {
      startKeyHolderChallenge(config.keyHolders.length + 1, name);
    }
  };
  
  // Dev mode: manually confirm (for testing without wallet)
  const confirmAddKeyHolderDevMode = () => {
    if (import.meta.env.DEV) {
      const newKeyHolder: KeyHolder = {
        id: `GKH-${config.keyHolders.length + 1}`,
        name: pendingKeyHolderName,
        publicKeyHex: `pk-dev-${Date.now().toString(16)}`,
        walletId: `wallet-dev-${Date.now().toString(16)}`,
        addedAt: new Date().toISOString(),
      };
      
      updateConfig('keyHolders', [...config.keyHolders, newKeyHolder]);
      cancelAddKeyHolder();
    }
  };
  
  const removeKeyHolder = (index: number) => {
    const newKeyHolders = config.keyHolders.filter((_, i) => i !== index);
    updateConfig('keyHolders', newKeyHolders);
  };
  
  // ============================================
  // Genesis Proposal
  // ============================================
  
  const submitGenesisProposal = async () => {
    if (config.keyHolders.length < 3) {
      setError('At least 3 key holders are required');
      return;
    }
    
    if (config.normalThreshold > config.keyHolders.length) {
      setError('Normal threshold cannot exceed number of key holders');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${edgeUrl}/api/genesis/proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config: {
            network_type: getNetworkType(),
            governance: {
              key_holders: config.keyHolders.map(kh => ({
                id: kh.id,
                name: kh.name,
                public_key_hex: kh.publicKeyHex,
              })),
              normal_threshold: config.normalThreshold,
              critical_threshold: config.criticalThreshold,
              action_cooldown_hours: config.actionCooldownHours,
            },
            timelocks: {
              deploy_minutes: config.deployTimelockMinutes,
              upgrade_hours: config.upgradeTimelockHours,
              emergency_minutes: config.emergencyResponseMinutes,
            },
            fail_closed: {
              failed_deployment_threshold: config.failedDeploymentThreshold,
              node_failure_percentage: config.nodeFailureThreshold,
              require_critical_for_recovery: config.requireCriticalForRecovery,
            },
            bootstrap: {
              minimum_nodes: config.minimumNodes,
              required_cloud_providers: config.requiredCloudProviders,
              required_regions: config.requiredRegions,
            },
          },
        }),
      });
      
      if (!response.ok) {
        // Create mock proposal for development
        const mockProposal: GenesisProposal = {
          proposalId: `genesis-${Date.now()}`,
          configHash: `hash-${Date.now().toString(16)}`,
          signatures: [],
          createdAt: new Date().toISOString(),
          status: 'pending',
        };
        setProposal(mockProposal);
        setCurrentStep('signatures');
        return;
      }
      
      const data = await response.json();
      setProposal({
        proposalId: data.proposal_id,
        configHash: data.config_hash,
        signatures: [],
        createdAt: data.created_at,
        status: 'pending',
      });
      setCurrentStep('signatures');
    } catch (err) {
      console.warn('[Genesis] Failed to submit proposal, using mock:', err);
      // Create mock proposal for development
      const mockProposal: GenesisProposal = {
        proposalId: `genesis-${Date.now()}`,
        configHash: `hash-${Date.now().toString(16)}`,
        signatures: [],
        createdAt: new Date().toISOString(),
        status: 'pending',
      };
      setProposal(mockProposal);
      setCurrentStep('signatures');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // ============================================
  // Signature Polling
  // ============================================
  
  const pollSignatures = useCallback(async () => {
    if (!proposal) return;
    
    try {
      const response = await fetch(`${edgeUrl}/api/genesis/proposal/${proposal.proposalId}/signatures`);
      
      if (!response.ok) return;
      
      const data = await response.json();
      setProposal(prev => prev ? {
        ...prev,
        signatures: data.signatures || [],
        status: data.status || prev.status,
      } : null);
    } catch {
      // Ignore polling errors
    }
  }, [proposal, edgeUrl]);
  
  useEffect(() => {
    if (!proposal || proposal.status === 'completed') return;
    
    const interval = setInterval(pollSignatures, 5000);
    return () => clearInterval(interval);
  }, [proposal, pollSignatures]);
  
  // ============================================
  // Genesis Completion
  // ============================================
  
  const completeGenesis = async () => {
    if (!proposal) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${edgeUrl}/api/genesis/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proposal_id: proposal.proposalId,
        }),
      });
      
      if (!response.ok) {
        // Simulate completion for development
        console.log('[Genesis] Simulating genesis completion');
        setTimeout(() => {
          navigate('/');
        }, 1000);
        return;
      }
      
      // Genesis complete - redirect to dashboard
      navigate('/');
    } catch (err) {
      console.warn('[Genesis] Failed to complete genesis:', err);
      setError('Failed to complete genesis. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // ============================================
  // Helpers (use module-level getNetworkName/getNetworkType)
  // ============================================
  
  const getSignedCount = () => {
    if (!proposal) return 0;
    return proposal.signatures.filter(s => s.verified).length;
  };
  
  const canComplete = () => {
    return getSignedCount() >= config.normalThreshold;
  };
  
  const exportConfig = () => {
    const exportData = {
      network: getNetworkName(),
      config,
      proposal: proposal ? {
        proposalId: proposal.proposalId,
        configHash: proposal.configHash,
        createdAt: proposal.createdAt,
      } : null,
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `genesis-${getNetworkName()}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const copySigningLink = async () => {
    if (!proposal) return;
    
    const signingUrl = `${window.location.origin}/sign-genesis/${proposal.proposalId}`;
    await navigator.clipboard.writeText(signingUrl);
    // Could show a toast here
  };
  
  // ============================================
  // Render
  // ============================================
  
  return (
    <div className="genesis-wizard">
      <header className="genesis-header">
        <h1>Genesis Wizard</h1>
        <span className="network-badge">{getNetworkName()}</span>
      </header>
      
      <nav className="genesis-steps">
        {steps.map((step, index) => (
          <button
            key={step.id}
            className={`step-indicator ${step.id === currentStep ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
            onClick={() => !proposal && setCurrentStep(step.id)}
            disabled={!!proposal && step.id !== 'signatures'}
          >
            <span className="step-number">{index + 1}</span>
            <span className="step-title">{step.title}</span>
          </button>
        ))}
      </nav>
      
      {error && (
        <div className="genesis-error">
          <span className="error-icon">!</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      
      <main className="genesis-content">
        {/* Step 1: Governance */}
        {currentStep === 'governance' && (
          <section className="step-content">
            <h2>Governance Configuration</h2>
            <p>Add key holders and configure voting thresholds for network governance.</p>
            
            <div className="form-group">
              <label>Key Holders ({config.keyHolders.length})</label>
              <div className="key-holders-list">
                {config.keyHolders.length === 0 && (
                  <p className="empty-message">No key holders added. Add 3-7 key holders to continue.</p>
                )}
                {config.keyHolders.map((kh, index) => (
                  <div key={kh.id} className="key-holder-item">
                    <div className="key-holder-info">
                      <span className="key-holder-name">{kh.name}</span>
                      <span className="key-holder-id">{kh.id}</span>
                    </div>
                    <button className="remove-btn" onClick={() => removeKeyHolder(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button 
                className="add-key-holder-btn" 
                onClick={startAddKeyHolder}
                disabled={config.keyHolders.length >= 7}
              >
                + Add Key Holder via Spark
              </button>
              {config.keyHolders.length > 0 && config.keyHolders.length < 3 && (
                <p className="warning-text">Add at least {3 - config.keyHolders.length} more key holder(s)</p>
              )}
            </div>
            
            <div className="threshold-group">
              <div className="form-group">
                <label>Normal Threshold</label>
                <input
                  type="number"
                  min={1}
                  max={config.keyHolders.length || 7}
                  value={config.normalThreshold}
                  onChange={e => updateConfig('normalThreshold', parseInt(e.target.value) || 1)}
                />
                <p className="help-text">Required signatures for routine operations</p>
              </div>
              
              <div className="form-group">
                <label>Critical Threshold</label>
                <input
                  type="number"
                  min={config.normalThreshold}
                  max={config.keyHolders.length || 7}
                  value={config.criticalThreshold}
                  onChange={e => updateConfig('criticalThreshold', parseInt(e.target.value) || config.normalThreshold)}
                />
                <p className="help-text">Required for key rotation, upgrades, emergency</p>
              </div>
              
              <div className="form-group">
                <label>Action Cooldown (hours)</label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={config.actionCooldownHours}
                  onChange={e => updateConfig('actionCooldownHours', parseInt(e.target.value) || 1)}
                />
                <p className="help-text">Minimum time between governance actions</p>
              </div>
            </div>
          </section>
        )}
        
        {/* Step 2: Time Delays */}
        {currentStep === 'delays' && (
          <section className="step-content">
            <h2>Time Delays</h2>
            <p>Configure timelocks to prevent rushed or malicious actions.</p>
            
            <div className="form-group">
              <label>Deploy Timelock (minutes)</label>
              <input
                type="number"
                min={0}
                max={1440}
                value={config.deployTimelockMinutes}
                onChange={e => updateConfig('deployTimelockMinutes', parseInt(e.target.value) || 0)}
              />
              <p className="help-text">Delay before deployment executes after approval</p>
            </div>
            
            <div className="form-group">
              <label>Upgrade Timelock (hours)</label>
              <input
                type="number"
                min={1}
                max={168}
                value={config.upgradeTimelockHours}
                onChange={e => updateConfig('upgradeTimelockHours', parseInt(e.target.value) || 1)}
              />
              <p className="help-text">Delay before protocol upgrade executes</p>
            </div>
            
            <div className="form-group">
              <label>Emergency Response (minutes)</label>
              <input
                type="number"
                min={5}
                max={60}
                value={config.emergencyResponseMinutes}
                onChange={e => updateConfig('emergencyResponseMinutes', parseInt(e.target.value) || 5)}
              />
              <p className="help-text">Faster timelock for emergency actions</p>
            </div>
          </section>
        )}
        
        {/* Step 3: Fail-Closed */}
        {currentStep === 'failclosed' && (
          <section className="step-content">
            <h2>Fail-Closed Rules</h2>
            <p>Configure automatic halt triggers for network protection.</p>
            
            <div className="form-group">
              <label>Failed Deployment Threshold</label>
              <input
                type="number"
                min={1}
                max={10}
                value={config.failedDeploymentThreshold}
                onChange={e => updateConfig('failedDeploymentThreshold', parseInt(e.target.value) || 1)}
              />
              <p className="help-text">Auto-halt after N failed deployments in 1 hour</p>
            </div>
            
            <div className="form-group">
              <label>Node Failure Threshold (%)</label>
              <input
                type="number"
                min={10}
                max={90}
                value={config.nodeFailureThreshold}
                onChange={e => updateConfig('nodeFailureThreshold', parseInt(e.target.value) || 10)}
              />
              <p className="help-text">Auto-halt when N% of nodes fail</p>
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={config.requireCriticalForRecovery}
                  onChange={e => updateConfig('requireCriticalForRecovery', e.target.checked)}
                />
                Require critical threshold for recovery
              </label>
              <p className="help-text">If checked, recovery from halt requires critical approval</p>
            </div>
          </section>
        )}
        
        {/* Step 4: Bootstrap */}
        {currentStep === 'bootstrap' && (
          <section className="step-content">
            <h2>Bootstrap Nodes</h2>
            <p>Define minimum infrastructure requirements for network launch.</p>
            
            <div className="form-group">
              <label>Minimum Anchor Nodes</label>
              <input
                type="number"
                min={3}
                max={10}
                value={config.minimumNodes}
                onChange={e => updateConfig('minimumNodes', parseInt(e.target.value) || 3)}
              />
              <p className="help-text">Minimum nodes required before genesis completes</p>
            </div>
            
            <div className="form-group">
              <label>Required Cloud Providers</label>
              <input
                type="number"
                min={1}
                max={3}
                value={config.requiredCloudProviders}
                onChange={e => updateConfig('requiredCloudProviders', parseInt(e.target.value) || 1)}
              />
              <p className="help-text">Minimum distinct providers (GCP, AWS, Azure)</p>
            </div>
            
            <div className="form-group">
              <label>Required Regions</label>
              <input
                type="number"
                min={1}
                max={5}
                value={config.requiredRegions}
                onChange={e => updateConfig('requiredRegions', parseInt(e.target.value) || 1)}
              />
              <p className="help-text">Minimum distinct geographic regions</p>
            </div>
            
            <div className="bootstrap-summary">
              <h3>Configuration Summary</h3>
              <ul>
                <li><strong>Key Holders:</strong> {config.keyHolders.length}</li>
                <li><strong>Thresholds:</strong> {config.normalThreshold}/{config.criticalThreshold}</li>
                <li><strong>Deploy Timelock:</strong> {config.deployTimelockMinutes} minutes</li>
                <li><strong>Upgrade Timelock:</strong> {config.upgradeTimelockHours} hours</li>
              </ul>
            </div>
          </section>
        )}
        
        {/* Step 5: Signatures */}
        {currentStep === 'signatures' && (
          <section className="step-content">
            <h2>Collect Signatures</h2>
            <p>Key holders must sign the genesis configuration to activate the network.</p>
            
            {!proposal ? (
              <div className="submit-proposal">
                <p>Review your configuration and submit the genesis proposal for signing.</p>
                <button 
                  className="btn-primary btn-lg"
                  onClick={submitGenesisProposal}
                  disabled={isSubmitting || config.keyHolders.length < 3}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Genesis Proposal'}
                </button>
              </div>
            ) : (
              <>
                <div className="proposal-info">
                  <div className="proposal-id">
                    <span className="label">Proposal ID</span>
                    <code>{proposal.proposalId}</code>
                  </div>
                  <div className="config-hash">
                    <span className="label">Config Hash</span>
                    <code>{proposal.configHash}</code>
                  </div>
                </div>
                
                <div className="signatures-list">
                  {config.keyHolders.map((kh) => {
                    const sig = proposal.signatures.find(s => s.keyHolderId === kh.id);
                    return (
                      <div key={kh.id} className={`signature-item ${sig?.verified ? 'signed' : 'pending'}`}>
                        <span className="sig-status">{sig?.verified ? '✓' : '○'}</span>
                        <div className="sig-info">
                          <span className="sig-name">{kh.name}</span>
                          <span className="sig-id">{kh.id}</span>
                        </div>
                        <span className="sig-label">{sig?.verified ? 'Signed' : 'Pending'}</span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="signature-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${(getSignedCount() / config.normalThreshold) * 100}%` }}
                    />
                  </div>
                  <p className="progress-text">
                    {getSignedCount()} / {config.normalThreshold} required signatures
                  </p>
                </div>
                
                <div className="signature-actions">
                  <button className="btn-secondary" onClick={exportConfig}>
                    Export Config
                  </button>
                  <button className="btn-secondary" onClick={copySigningLink}>
                    Copy Signing Link
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={completeGenesis}
                    disabled={!canComplete() || isSubmitting}
                  >
                    {isSubmitting ? 'Completing...' : 'Complete Genesis'}
                  </button>
                </div>
              </>
            )}
          </section>
        )}
      </main>
      
      <footer className="genesis-footer">
        <button 
          className="btn-secondary"
          onClick={prevStep}
          disabled={currentStepIndex === 0 || !!proposal}
        >
          Previous
        </button>
        
        {currentStep === 'bootstrap' ? (
          <button
            className="btn-primary"
            onClick={() => setCurrentStep('signatures')}
            disabled={config.keyHolders.length < 3}
          >
            Review & Sign
          </button>
        ) : (
          <button
            className="btn-primary"
            onClick={nextStep}
            disabled={currentStepIndex === steps.length - 1}
          >
            Next
          </button>
        )}
      </footer>
      
      {/* Spark Scanner Modal */}
      {showSparkScanner && (
        <div className="spark-modal-overlay" onClick={cancelAddKeyHolder}>
          <div className="spark-modal" onClick={e => e.stopPropagation()}>
            <div className="spark-modal-header">
              <h3>Add Key Holder</h3>
              <button className="close-btn" onClick={cancelAddKeyHolder}>×</button>
            </div>
            
            <div className="spark-modal-body">
              <div className="form-group">
                <label>Key Holder Name</label>
                <input
                  type="text"
                  value={pendingKeyHolderName}
                  onChange={e => updateKeyHolderName(e.target.value)}
                  placeholder="e.g., Founder, Security Lead"
                />
              </div>
              
              <p className="scan-instructions">
                Scan this Spark code with the eStream Wallet app to register as a key holder.
              </p>
              
              <div className="spark-container">
                {keyHolderState.status === 'pending' && keyHolderChallengeJson ? (
                  <>
                    <SparkRenderer
                      challengeJson={keyHolderChallengeJson}
                      variant="genesis"
                      size={280}
                      showCode
                    />
                    {keyHolderTimeRemaining !== null && (
                      <p className="time-remaining">Expires in {keyHolderTimeRemaining}s</p>
                    )}
                  </>
                ) : keyHolderState.status === 'expired' ? (
                  <div className="spark-expired">
                    <span className="expired-icon">⏰</span>
                    <span>Challenge expired</span>
                    <button 
                      className="btn-secondary" 
                      onClick={() => startKeyHolderChallenge(config.keyHolders.length + 1, pendingKeyHolderName)}
                    >
                      Retry
                    </button>
                  </div>
                ) : keyHolderState.status === 'error' ? (
                  <div className="spark-error">
                    <span className="error-icon">!</span>
                    <span>{keyHolderState.error}</span>
                    <button 
                      className="btn-secondary" 
                      onClick={() => startKeyHolderChallenge(config.keyHolders.length + 1, pendingKeyHolderName)}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="spark-loading">
                    <div className="spinner" />
                    <span>Generating Spark...</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="spark-modal-footer">
              <button className="btn-secondary" onClick={cancelAddKeyHolder}>
                Cancel
              </button>
              {import.meta.env.DEV && (
                <button 
                  className="btn-primary" 
                  onClick={confirmAddKeyHolderDevMode}
                  disabled={!pendingKeyHolderName.trim()}
                >
                  Confirm (Dev Mode)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
